import { BadgeCheck, Clock3, CreditCard, Landmark, RotateCcw } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactForm from "../components/ContactForm";
import { pricingComplianceNotes, servicePackages } from "../content/services";

const accentMap = {
  blue: "from-blue-600 to-cyan-500",
  indigo: "from-indigo-600 to-blue-500",
  cyan: "from-cyan-600 to-sky-500",
  red: "from-rose-600 to-orange-500",
  violet: "from-violet-600 to-fuchsia-500",
  emerald: "from-emerald-600 to-teal-500",
};

const detailCards = [
  {
    icon: CreditCard,
    title: "Transparent AED Pricing",
    text: "All listed service rates are presented in AED for payment gateway review and customer clarity.",
  },
  {
    icon: Landmark,
    title: "Taxes & Charges",
    text: "VAT, bank charges, and any required regulatory fees are disclosed before payment confirmation where applicable.",
  },
  {
    icon: RotateCcw,
    title: "Refund Eligibility",
    text: "Refunds depend on the service type, delivery stage, and whether work or provisioning has already started.",
  },
  {
    icon: Clock3,
    title: "Delivery Timelines",
    text: "Indicative turnaround times are shown for every package so customers understand execution expectations upfront.",
  },
];

export default function Pricing() {
  return (
    <div className="font-sans bg-gradient-to-b from-slate-50 via-white to-slate-100 text-slate-900">
      <Navbar />

      <section className="relative overflow-hidden bg-slate-950 pt-28 pb-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.28),_transparent_28%),radial-gradient(circle_at_bottom_right,_rgba(16,185,129,0.18),_transparent_24%)]" />
        <div className="relative mx-auto max-w-6xl px-6">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-sky-100">
              Products & Services
            </span>
            <h1 className="mt-6 text-4xl font-extrabold leading-tight md:text-6xl">
              Pricing, delivery, and refund details presented for UAE payment compliance.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-300">
              Review our active service offerings, package structure, starting prices, delivery timelines, applicable tax guidance, and refund eligibility before initiating payment.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {detailCards.map(({ icon: Icon, title, text }) => (
              <div
                key={title}
                className="rounded-[1.75rem] border border-white/10 bg-white/8 p-6 backdrop-blur-sm"
              >
                <Icon className="h-9 w-9 text-sky-300" />
                <h2 className="mt-4 text-xl font-semibold text-white">{title}</h2>
                <p className="mt-2 text-sm leading-7 text-slate-300">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-20">
        <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-blue-600">
              Service Catalog
            </p>
            <h2 className="mt-2 text-3xl font-extrabold text-slate-900 md:text-4xl">
              Dedicated pricing cards for every listed service
            </h2>
          </div>
          <p className="max-w-2xl text-sm leading-7 text-slate-500">
            Final quotations may vary based on scope, integrations, urgency, and compliance review requirements. Any custom quotation will be shared before payment confirmation.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {servicePackages.map((service) => (
            <article
              key={service.name}
              className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm"
            >
              <div className={`bg-gradient-to-r ${accentMap[service.color]} px-7 py-7 text-white`}>
                <p className="text-xs uppercase tracking-[0.22em] text-white/80">Service Name</p>
                <h3 className="mt-2 text-2xl font-bold">{service.name}</h3>
                <p className="mt-3 max-w-2xl text-sm leading-7 text-white/85">{service.description}</p>
              </div>

              <div className="space-y-5 px-7 py-7">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-3xl bg-slate-50 p-5">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Price</p>
                    <p className="mt-2 text-2xl font-extrabold text-slate-900">{service.price}</p>
                    <p className="mt-2 text-sm text-slate-500">Quoted in AED</p>
                  </div>
                  <div className="rounded-3xl bg-slate-50 p-5">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Delivery Timeline</p>
                    <p className="mt-2 text-2xl font-extrabold text-slate-900">{service.deliveryTimeline}</p>
                    <p className="mt-2 text-sm text-slate-500">Subject to scope confirmation</p>
                  </div>
                </div>

                <div className="rounded-3xl border border-slate-200 p-5">
                  <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Subscription / Package Details</p>
                  <p className="mt-3 text-sm leading-7 text-slate-700">{service.subscription}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {service.features.map((feature) => (
                      <span
                        key={feature}
                        className="rounded-full bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700"
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-5">
                    <p className="text-xs uppercase tracking-[0.18em] text-emerald-700">Refund Policy</p>
                    <p className="mt-3 text-sm leading-7 text-emerald-900">{service.refundPolicy}</p>
                  </div>
                  <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5">
                    <p className="text-xs uppercase tracking-[0.18em] text-amber-700">Taxes & Charges</p>
                    <p className="mt-3 text-sm leading-7 text-amber-900">
                      VAT and any applicable gateway, bank, or regulatory charges will be shown before payment confirmation.
                    </p>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pb-20">
        <div className="rounded-[2rem] border border-slate-200 bg-white p-8 shadow-sm">
          <div className="flex items-center gap-3">
            <BadgeCheck className="h-8 w-8 text-blue-600" />
            <h2 className="text-2xl font-bold text-slate-900">Pricing Compliance Notes</h2>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {pricingComplianceNotes.map((note) => (
              <div key={note} className="rounded-3xl bg-slate-50 p-5 text-sm leading-7 text-slate-700">
                {note}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ContactForm />
      <Footer />
    </div>
  );
}
